import type { Result } from 'neverthrow';

import { apiRequest, type ApiError } from './client';

/**
 * Public-shape Label record + presign that the labels card on the
 * order-detail page consumes. Mirrors `apps/api/src/handlers/list-order-labels.ts`
 * `LabelWithDownload`. The download URL is a short-TTL presigned S3
 * URL — refetch the list to get a fresh one if the user takes a long
 * time to click.
 */
export interface OrderLabel {
  readonly label: {
    readonly label_id: string;
    readonly tenant_id: string;
    readonly shipment_id: string;
    readonly format: 'pdf' | 'zpl';
    readonly s3_bucket: string;
    readonly s3_key: string;
    readonly tracking_number: string;
    readonly voided: boolean;
    readonly created_at: string;
  };
  readonly shipment_status: 'labelled' | 'shipped' | 'voided';
  readonly carrier: 'royal_mail';
  readonly download_url: string;
}

export interface ListOrderLabelsResponse {
  readonly labels: readonly OrderLabel[];
}

export const ORDER_LABELS_QUERY_KEY = (orderId: string) => ['order-labels', orderId] as const;

export function listOrderLabels(
  orderId: string,
): Promise<Result<ListOrderLabelsResponse, ApiError>> {
  return apiRequest<ListOrderLabelsResponse>({
    method: 'GET',
    path: `/orders/${encodeURIComponent(orderId)}/labels`,
  });
}

export interface GenerateLabelResponse {
  readonly label: OrderLabel['label'];
  readonly shipment: {
    readonly shipment_id: string;
    readonly tenant_id: string;
    readonly order_id: string;
    readonly carrier: 'royal_mail';
    readonly status: 'labelled' | 'shipped' | 'voided';
    readonly created_at: string;
    readonly updated_at: string;
  };
  readonly download_url: string;
}

export function generateLabel(orderId: string): Promise<Result<GenerateLabelResponse, ApiError>> {
  return apiRequest<GenerateLabelResponse>({
    method: 'POST',
    path: `/orders/${encodeURIComponent(orderId)}/labels`,
  });
}
